import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { ContactUsModal } from '@/components/ContactUsModal';
import { useEditableMarkdown } from '@/shared/EditableMarkdown';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import { ProfileForm, toggleInArray } from '@/shared/onboarding';
import { useProfileService } from '@/services/regulatoryWatch';
import { Check, Loader2, Pencil } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { ProductListDetail } from './ProductListDetail';

export const CompanyProfileTab: FC = () => {
  const { profile, isProfileLoading, updateProfile, isUpdating } =
    useProfileService();

  const [companyDescription, setCompanyDescription] = useState<string>('');
  const [markets, setMarkets] = useState<string[]>([]);
  const [riskClassifications, setRiskClassifications] = useState<string[]>([]);
  const [deviceCharacteristics, setDeviceCharacteristics] = useState<string[]>(
    []
  );
  const [therapeuticAreas, setTherapeuticAreas] = useState<string[]>([]);
  const [contactUsOpen, setContactUsOpen] = useState(false);

  const hasUserEditedProfile = useRef(false);
  const [showProfileSaved, setShowProfileSaved] = useState(false);
  const wasUpdating = useRef(false);
  const descriptionDebounceRef =
    useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (wasUpdating.current && !isUpdating) {
      setShowProfileSaved(true);
      const timer = setTimeout(() => setShowProfileSaved(false), 2000);
      return () => clearTimeout(timer);
    }
    wasUpdating.current = isUpdating;
  }, [isUpdating]);

  useEffect(() => {
    if (profile) {
      setCompanyDescription(profile.companyDescription ?? '');
      setMarkets(profile.markets ?? []);
      setRiskClassifications(profile.riskClassifications ?? []);
      setDeviceCharacteristics(profile.deviceCharacteristics ?? []);
      setTherapeuticAreas(profile.therapeuticAreas ?? []);
    }
  }, [profile]);

  useEffect(() => {
    if (!hasUserEditedProfile.current) return;

    const timer = setTimeout(() => {
      hasUserEditedProfile.current = false;
      updateProfile({
        markets,
        riskClassifications,
        deviceCharacteristics,
        therapeuticAreas,
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    markets,
    riskClassifications,
    deviceCharacteristics,
    therapeuticAreas,
    updateProfile,
  ]);

  const markEdited = () => {
    hasUserEditedProfile.current = true;
  };

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setCompanyDescription(value);
      clearTimeout(descriptionDebounceRef.current);
      descriptionDebounceRef.current = setTimeout(() => {
        updateProfile({ companyDescription: value });
      }, 800);
    },
    [updateProfile]
  );

  useEffect(() => {
    return () => clearTimeout(descriptionDebounceRef.current);
  }, []);

  const {
    isEditing: isEditingDescription,
    textareaProps: descriptionTextareaProps,
    onPreviewClick: onDescriptionPreviewClick,
    onPreviewKeyDown: onDescriptionPreviewKeyDown,
  } = useEditableMarkdown({
    value: companyDescription,
    onChange: handleDescriptionChange,
  });

  if (isProfileLoading) {
    return (
      <FlexCol alignItems="center" justifyContent="center" className="py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </FlexCol>
    );
  }

  return (
    <FlexCol gap="6">
      <div>
        <FlexRow alignItems="center" justifyContent="between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">
              Company profile
            </h2>
            <Text variant="body-sm" color="secondary" className="mt-1">
              Your company profile helps us personalize your experience.
            </Text>
          </div>
          <div className="h-5">
            {isUpdating && (
              <FlexRow alignItems="center" gap="1">
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                <Text variant="body-xs" color="secondary">
                  Saving...
                </Text>
              </FlexRow>
            )}
            {showProfileSaved && !isUpdating && (
              <FlexRow alignItems="center" gap="1">
                <Check className="h-3 w-3 text-secondary" />
                <Text variant="body-xs" className="text-secondary">
                  Saved
                </Text>
              </FlexRow>
            )}
          </div>
        </FlexRow>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-neutral-900">
          Company description
        </label>
        {isEditingDescription ? (
          <Textarea
            {...descriptionTextareaProps}
            className="min-h-[120px] w-full resize-none border-neutral-300 overflow-hidden"
            placeholder="Describe your company..."
          />
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={onDescriptionPreviewClick}
            onKeyDown={onDescriptionPreviewKeyDown}
            className="group relative min-h-[120px] w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 cursor-pointer transition-colors hover:border-dashed hover:border-neutral-400 hover:bg-neutral-100/50"
          >
            <Pencil className="absolute top-3 right-3 size-3.5 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100" />
            {companyDescription ? (
              <MarkdownText size="sm">{companyDescription}</MarkdownText>
            ) : (
              <span className="text-sm text-neutral-500">
                Click to add a company description...
              </span>
            )}
          </div>
        )}
      </div>

      <ProfileForm
        onRequestNewRegion={() => setContactUsOpen(true)}
        markets={markets}
        onToggleMarket={(market: string) => {
          markEdited();
          setMarkets(toggleInArray(markets, market));
        }}
        riskClassifications={riskClassifications}
        onSetRiskClassifications={(value: string[]) => {
          markEdited();
          setRiskClassifications(value);
        }}
        deviceCharacteristics={deviceCharacteristics}
        onSetDeviceCharacteristics={(value: string[]) => {
          markEdited();
          setDeviceCharacteristics(value);
        }}
        therapeuticAreas={therapeuticAreas}
        onSetTherapeuticAreas={(value: string[]) => {
          markEdited();
          setTherapeuticAreas(value);
        }}
        hideDescription
      />

      <ProductListDetail products={profile?.products ?? []} />

      <ContactUsModal open={contactUsOpen} onOpenChange={setContactUsOpen} />
    </FlexCol>
  );
};
